import React from 'react';
import { TrendingUp, Zap, DollarSign, Clock, AlertTriangle } from 'lucide-react';
import { Lead } from './mockLeads';

interface KPICardProps {
  label: string;
  value: string;
  sub: string;
  icon: React.ReactNode;
  trend?: { dir: 'up' | 'down' | 'neutral'; label: string };
  accent: 'blue' | 'gold' | 'red' | 'green' | 'purple';
  alert?: boolean;
}

function KPICard({ label, value, sub, icon, trend, accent, alert }: KPICardProps) {
  const accentColors = {
    blue: { bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-500/20' },
    gold: { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/20' },
    red: { bg: 'bg-red-500/10', text: 'text-red-400', border: 'border-red-500/20' },
    green: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/20' },
    purple: { bg: 'bg-purple-500/10', text: 'text-purple-400', border: 'border-purple-500/20' },
  };
  const colors = accentColors[accent];

  return (
    <div className={`leverage-card p-5 relative overflow-hidden ${alert ? 'border-red-500/30' : ''}`}>
      {alert && (
        <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-red-500 to-red-400" />
      )}
      <div className="flex items-start justify-between mb-3">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${colors.bg} border ${colors.border}`}>
          <span className={colors.text}>{icon}</span>
        </div>
        {alert && <AlertTriangle size={14} className="text-red-400" />}
        {trend && !alert && (
          <span className={`text-xs font-600 ${trend.dir === 'up' ? 'text-emerald-400' : trend.dir === 'down' ? 'text-red-400' : 'text-[#666]'}`}>
            {trend.dir === 'up' ? '↑' : trend.dir === 'down' ? '↓' : '→'} {trend.label}
          </span>
        )}
      </div>
      <p className="text-[12px] text-[#666] font-500 uppercase tracking-wide mb-1">{label}</p>
      <p className="text-3xl font-800 text-white tabular-nums mb-1">{value}</p>
      <p className="text-xs text-[#555]">{sub}</p>
    </div>
  );
}

export default function DashboardKPIs({ leads }: { leads: Lead[] }) {
  const totalThisWeek = leads.length;
  const highLeverage = leads.filter((l) => l.leverageScore === 'High').length;
  const newUnreviewed = leads.filter((l) => l.status === 'New').length;
  const avgEstimate = Math.round(
    leads.reduce((sum, l) => sum + (l.estimateMin + l.estimateMax) / 2, 0) / leads.length
  );
  const wonLeads = leads.filter((l) => l.status === 'Won').length;
  const conversionRate = Math.round((wonLeads / leads.length) * 100);

  // Grid plan: 4 cards → grid-cols-2 lg:grid-cols-4
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      <KPICard
        label="Total Leads"
        value={String(totalThisWeek)}
        sub="This week · all sources"
        icon={<TrendingUp size={18} />}
        trend={{ dir: 'up', label: '+3 vs last week' }}
        accent="blue"
      />
      <KPICard
        label="High Leverage"
        value={String(highLeverage)}
        sub={`${newUnreviewed} still uncontacted`}
        icon={<Zap size={18} />}
        accent="gold"
        alert={newUnreviewed >= 3}
      />
      <KPICard
        label="Avg Estimate"
        value={`$${(avgEstimate / 1000).toFixed(1)}k`}
        sub="Across all open leads"
        icon={<DollarSign size={18} />}
        trend={{ dir: 'up', label: '+$420 vs last week' }}
        accent="green"
      />
      <KPICard
        label="Close Rate"
        value={`${conversionRate}%`}
        sub={`${wonLeads} of ${totalThisWeek} leads won`}
        icon={<Clock size={18} />}
        trend={{ dir: conversionRate >= 20 ? 'up' : 'down', label: `${conversionRate >= 20 ? '+' : '-'}2% vs avg` }}
        accent="purple"
      />
    </div>
  );
}